import React from 'react';
import Icon from '../../../components/AppIcon';

const MissionStatement = () => {
  const philosophyPoints = [
    {
      icon: "Target",
      title: "Purpose-Driven Development",
      description: "Every line of code should serve a meaningful purpose and create genuine value for users."
    },
    {
      icon: "Users",
      title: "Human-Centered Design",
      description: "Technology should enhance human capabilities, not complicate them. User experience is paramount."
    },
    {
      icon: "Zap",
      title: "Innovation with Impact",
      description: "Embrace cutting-edge technologies while ensuring they solve real-world problems effectively."
    },
    {
      icon: "Shield",
      title: "Quality & Reliability",
      description: "Build robust, scalable solutions that stand the test of time and perform under pressure."
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Compass" size={16} />
            <span>My Development Philosophy</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Building the Future,
            <span className="text-gradient-brand block">One Line at a Time</span>
          </h2>
          
          <p className="text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
            My mission is to create digital experiences that don't just function—they inspire, empower, and transform. 
            I believe that the intersection of technical excellence and creative vision is where true innovation happens.
          </p>
        </div>

        {/* Mission Statement Card */}
        <div className="bg-gradient-brand rounded-3xl p-8 lg:p-12 mb-16 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl"></div>
          
          <div className="relative z-10 text-center">
            <Icon name="Quote" size={48} className="mx-auto mb-6 opacity-80" />
            <blockquote className="text-xl lg:text-2xl font-medium leading-relaxed mb-6">
            "I don't just write code—I build developer tools and digital experiences that empower others to create, learn, and innovate. Every project is an opportunity to push boundaries and deliver real-world impact."
            </blockquote>
          <cite className="text-lg font-semibold">— Mustan Aliev, Full-Stack Engineer & Open Source Advocate</cite>
          </div>
        </div>

        {/* Philosophy Points */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {philosophyPoints?.map((point, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-2xl p-6 hover-lift transition-brand group"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-brand">
                <Icon name={point?.icon} size={24} className="text-primary" />
              </div>
              
              <h3 className="text-lg font-semibold text-card-foreground mb-3">
                {point?.title}
              </h3>
              
              <p className="text-muted-foreground leading-relaxed">
                {point?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Core Values */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-8">
            Core Values That Drive My Work
          </h3>
          
          <div className="flex flex-wrap justify-center gap-4">
            {[
              "Continuous Learning",
              "Collaborative Spirit",
              "Attention to Detail",
              "User Empathy",
              "Technical Excellence",
              "Creative Problem Solving"
            ]?.map((value, index) => (
              <div
                key={index}
                className="bg-accent/10 text-accent px-6 py-3 rounded-full text-sm font-medium hover:bg-accent/20 transition-brand cursor-default"
              >
                {value}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionStatement;
